import * as React from "react";
import { proxy, useSnapshot } from "valtio";
import { okhiTracker } from "../OkHiTracker";

type UserLocationState = {
  status: "idle" | "loading" | "success" | "error";
  location: { lat: number; lng: number; accuracy: number } | null;
};

export const userLocationState = proxy<UserLocationState>({
  status: "idle",
  location: null,
});

export const useUserLocation = () => {
  const userLocationSnapshot = useSnapshot(userLocationState);
  const onSuccess = React.useCallback((location) => {
    const {
      coords: { longitude, latitude, accuracy },
    } = location;
    const formattedLocation = {
      lat: latitude,
      lng: longitude,
      accuracy,
    };
    userLocationState.status = "success";
    userLocationState.location = formattedLocation;
  }, []);
  const onError = React.useCallback((error) => {
    if (userLocationState.status !== "success") {
      userLocationState.status = "error";
    }
    okhiTracker.track("GeoPoint Not Found", {
      code: error.code ? error.code : "N/A",
      message: error.message ? error.message : "N/A",
      onObject: "gps",
    });
  }, []);
  const watchUserLocation = React.useCallback(
    ({ timeout = 10000 } = {}) => {
      if (userLocationState.status !== "success") {
        userLocationState.status = "loading";
      }
      return navigator.geolocation.watchPosition(onSuccess, onError, {
        timeout,
        enableHighAccuracy: true,
      });
    },
    [onSuccess, onError]
  );

  React.useEffect(() => {
    const watchId = watchUserLocation();
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchUserLocation]);

  return { userLocationSnapshot };
};
